import { AdSlotType } from '../../../advertising/AdTypes';
import { WORLD_CONFIG } from '../../../config/world.config';
import { DynamicEventType } from '../../../dynamic-events/DynamicEventType';
import { ObstacleType } from '../../../obstacles/ObstacleType';
import { DEG_TO_RAD } from '../../../utils/math';
import { ConnectorProfile, ConnectorRole } from '../../connectors/ConnectorDefinition';
import {
  BACKDROP_SOCKET,
  COLLECTIBLE_FLOAT_HEIGHT,
  CRANE_SOCKET,
  SITE_LAYOUT,
  backdropSocket,
  collectibleLanes,
  craneSocket,
  nearSideSockets,
  obstacleRow,
  standardLanes,
} from '../segmentBuilders';
import { SegmentKind, type SegmentDefinition } from '../SegmentDefinition';

/**
 * SEG_COLLAPSE_40M – set piece "Hauseinsturz".
 *
 * A three-storey shell building stands left of the track. When the player is
 * ~55 m before its center, it collapses: walls, slabs and blocks tumble onto
 * the track and stay there as three hand-designed obstacle rows.
 *
 *   Row R1 z=14:  L0 wall (lane change) · L1 block (jump) · L2 free
 *   Row R2 z=24:  L0 block (jump)       · L1 slab (slide) · L2 wall (lane change)
 *   Row R3 z=34:  L0 wall (lane change) · L1 free         · L2 block (jump)
 *
 * The rows are validated against the sequence rules at max speed (unit test).
 */
const LENGTH = 40;
const FACE_LEFT = -90 * DEG_TO_RAD;
const DEBRIS_TYPES = [ObstacleType.Jump, ObstacleType.Slide, ObstacleType.LaneChange] as const;

export const SEG_COLLAPSE_40M: SegmentDefinition = {
  id: 'SEG_COLLAPSE_40M',
  kind: SegmentKind.Straight,
  displayName: 'Hauseinsturz 40 m',
  length: LENGTH,
  width: WORLD_CONFIG.trackWidth,
  visualAssetId: 'SEG_COLLAPSE_40M',

  entry: {
    id: 'CONNECTOR_IN',
    role: ConnectorRole.In,
    profile: ConnectorProfile.Ground3Lane,
    transform: { x: 0, y: 0, z: 0, yaw: 0 },
    allowedNextProfiles: [],
  },
  exits: [
    {
      id: 'CONNECTOR_OUT',
      role: ConnectorRole.Out,
      profile: ConnectorProfile.Ground3Lane,
      transform: { x: 0, y: 0, z: LENGTH, yaw: 0 },
      allowedNextProfiles: [ConnectorProfile.Ground3Lane],
    },
  ],

  lanes: standardLanes(WORLD_CONFIG),

  obstacleSpawnPoints: [
    ...obstacleRow(WORLD_CONFIG, 'R1', 14, DEBRIS_TYPES),
    ...obstacleRow(WORLD_CONFIG, 'R2', 24, DEBRIS_TYPES),
    ...obstacleRow(WORLD_CONFIG, 'R3', 34, DEBRIS_TYPES),
  ],
  obstacleRowLayouts: { sparse: [], dense: [] },
  fixedObstacles: [
    { spawnPointId: 'SOCKET_OBS_R1_L0', obstacleId: 'OBS_DEBRIS_WALL_A' },
    { spawnPointId: 'SOCKET_OBS_R1_L1', obstacleId: 'OBS_DEBRIS_BLOCK_A' },
    { spawnPointId: 'SOCKET_OBS_R2_L0', obstacleId: 'OBS_DEBRIS_BLOCK_A' },
    { spawnPointId: 'SOCKET_OBS_R2_L1', obstacleId: 'OBS_DEBRIS_SLAB_A' },
    { spawnPointId: 'SOCKET_OBS_R2_L2', obstacleId: 'OBS_DEBRIS_WALL_A' },
    { spawnPointId: 'SOCKET_OBS_R3_L0', obstacleId: 'OBS_DEBRIS_WALL_A' },
    { spawnPointId: 'SOCKET_OBS_R3_L2', obstacleId: 'OBS_DEBRIS_BLOCK_A' },
  ],

  collectibleSpawnPoints: collectibleLanes(WORLD_CONFIG, 1, LENGTH - 1, 2, COLLECTIBLE_FLOAT_HEIGHT),

  // Building side (left) stays free for the ruin; roadside bays only on the right (ids like moduleSides).
  decorationSockets: [
    ...nearSideSockets('RIGHT', '', 0),
    ...nearSideSockets('RIGHT', '_2', SITE_LAYOUT.blockLength),
    backdropSocket('SOCKET_DECO_R_04', 'RIGHT', BACKDROP_SOCKET.x, LENGTH / 4, FACE_LEFT),
    backdropSocket('SOCKET_DECO_R_05', 'RIGHT', BACKDROP_SOCKET.x, (3 * LENGTH) / 4, FACE_LEFT),
    craneSocket('SOCKET_DECO_R_06', 'RIGHT', CRANE_SOCKET.x, LENGTH / 2, Math.PI),
  ],

  dynamicEventSockets: [
    {
      id: 'SOCKET_EVT_COLLAPSE',
      position: { x: -12, y: 0, z: 22 },
      yaw: -FACE_LEFT,
      supportedEvents: [DynamicEventType.WallCollapse],
      triggerDistance: 55,
      affectedLanes: [0, 1, 2],
    },
  ],

  advertisingSlots: [
    {
      id: 'AD_FENCE_RIGHT',
      slotType: AdSlotType.FenceRight,
      position: { x: 4.12, y: 1.15, z: 20 },
      yaw: FACE_LEFT,
      width: 6,
      height: 1.5,
    },
  ],

  collapseEvent: {
    socketId: 'SOCKET_EVT_COLLAPSE',
    // Building: three storeys of ~3.6 m, façade along x = -10, back at x = -14.
    pieces: [
      { spawnPointId: 'SOCKET_OBS_R2_L2', buildingPosition: { x: -10, y: 0, z: 26 }, buildingRotation: { x: 0, y: 90, z: 0 }, finalYaw: 6, delay: 0 },
      { spawnPointId: 'SOCKET_OBS_R3_L0', buildingPosition: { x: -10, y: 0, z: 33 }, buildingRotation: { x: 0, y: 90, z: 0 }, finalYaw: -8, delay: 0.12 },
      { spawnPointId: 'SOCKET_OBS_R1_L0', buildingPosition: { x: -10, y: 3.6, z: 15 }, buildingRotation: { x: 0, y: 90, z: 0 }, finalYaw: 10, delay: 0.2 },
      { spawnPointId: 'SOCKET_OBS_R2_L1', buildingPosition: { x: -12, y: 3.6, z: 23 }, buildingRotation: { x: 0, y: 0, z: 0 }, finalYaw: -4, delay: 0.32 },
      { spawnPointId: 'SOCKET_OBS_R2_L0', buildingPosition: { x: -11, y: 7.2, z: 21 }, buildingRotation: { x: 0, y: 90, z: 0 }, finalYaw: 14, delay: 0.42 },
      { spawnPointId: 'SOCKET_OBS_R1_L1', buildingPosition: { x: -12.5, y: 7.2, z: 15 }, buildingRotation: { x: 0, y: 90, z: 0 }, finalYaw: -12, delay: 0.5 },
      { spawnPointId: 'SOCKET_OBS_R3_L2', buildingPosition: { x: -12, y: 10.8, z: 30 }, buildingRotation: { x: 0, y: 90, z: 0 }, finalYaw: 9, delay: 0.6 },
    ],
  },

  generation: {
    weight: 3,
    minDistance: 2000,
    cooldownDistance: 2000,
    minSegmentsAfterJunction: 2,
    // user 2026-10-03: "dass wir diese Elemente dann vielleicht auch zweimal hintereinander reinlaufen lassen" – every
    // other collapse is a pair (20 m of free road between the last row and the next first row, unit test); the cooldown
    // counts from the second one.
    chain: {
      lengths: [
        [1, 0.5],
        [2, 0.5],
      ],
    },
    tags: ['set-piece', 'collapse'],
  },
};
